import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import {
  trackPageViewV2,
  trackUserInteraction,
} from "@/services/analytics-adapter";
import {
  VoucherSubType,
  VoucherType,
  VoucherVendorDefinitionId,
  isDoctorConsultType,
} from "@/services/core-api-adapter";
import { defaultFullscreenPageStyling, panelStyle } from "@/theme";
import { formatAmount } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface VoucherCardProps {
  voucherSubType: VoucherSubType;
  voucherSubTypeKey: string | null;
  onClick: (voucherSubType: VoucherSubType) => void;
}

function VoucherCard({
  voucherSubType,
  voucherSubTypeKey,
  onClick,
}: VoucherCardProps) {
  const { t } = useTranslation();
  const isUnknownType = !voucherSubTypeKey;

  const title = voucherSubTypeKey
    ? t(`DoctorNurseVoucherTypeSelection.${voucherSubTypeKey}.title` as any)
    : voucherSubType.name;
  const description = voucherSubTypeKey
    ? t(
        `DoctorNurseVoucherTypeSelection.${voucherSubTypeKey}.description` as any
      )
    : voucherSubType.description;

  return (
    <Stack
      key={voucherSubType.voucherSubTypeId}
      component="button"
      disabled={isUnknownType}
      sx={{
        ...panelStyle,
        cursor: isUnknownType ? "default" : "pointer",
        border: "none",
        opacity: isUnknownType ? 0.5 : 1,
        pointerEvents: isUnknownType ? "none" : "auto",
      }}
      aria-label={title}
      color="neutral"
      onClick={() => onClick(voucherSubType)}
    >
      <Stack sx={{ width: "100%" }} spacing={2}>
        <Stack spacing={1}>
          <Stack
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            gap={1}
            alignItems="left"
          >
            <Typography variant="h3" fontWeight={600} textAlign="left">
              {title}
            </Typography>
            {isDoctorConsultType(voucherSubType.voucherVendorDefinitionId) && (
              <Stack
                sx={{
                  backgroundColor: "accent1.main",
                  borderRadius: 2,
                  alignSelf: "start",
                }}
                padding={1}
              >
                <Typography
                  fontWeight={600}
                  variant="body2"
                  color="primary.contrastText"
                >
                  {t("common.mostPopular")}
                </Typography>
              </Stack>
            )}
            {voucherSubType.voucherVendorDefinitionId ===
              VoucherVendorDefinitionId.DOCTOR_MEDICINE_CONSULT && (
              <Stack
                sx={{
                  backgroundColor: "accent1.main",
                  borderRadius: 2,
                  alignSelf: "start",
                }}
                padding={1}
              >
                <Typography
                  fontWeight={600}
                  variant="body2"
                  color="primary.contrastText"
                >
                  {t("common.new")}
                </Typography>
              </Stack>
            )}
          </Stack>

          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body1" color="neutral.light" textAlign="left">
              {description}
            </Typography>
            <IconLoader
              sx={{ fontSize: 30 }}
              color="primary"
              icon="ChevronNextIcon"
            />
          </Stack>
        </Stack>

        <Box
          textAlign="center"
          sx={{
            background: (theme) => theme.palette.primary.main,
            width: 90,
            borderRadius: 2,
            padding: 1,
          }}
        >
          <Typography color="primary.contrastText" fontWeight={600}>
            {formatAmount(voucherSubType.amount, "ZAR")}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

interface DoctorNurseVoucherTypeSelectionProps {
  onDoctorConsultClick: (selectedVoucherSubType: VoucherSubType) => void;
  onNurseConsultClick: (selectedVoucherSubType: VoucherSubType) => void;
  onDocMedBundleConsultClick: (selectedVoucherSubType: VoucherSubType) => void;
  selectedVoucher: VoucherType | undefined;
  onBackButtonClick: () => void;
}

export function DoctorNurseVoucherTypeSelection({
  onDoctorConsultClick,
  onNurseConsultClick,
  onDocMedBundleConsultClick,
  selectedVoucher,
  onBackButtonClick,
}: DoctorNurseVoucherTypeSelectionProps) {
  const { t } = useTranslation();

  function handleVoucherSubTypeClick(voucherSubType: VoucherSubType) {
    trackUserInteraction({
      linkText: `DoctorNurseVoucherTypeSelection | ${voucherSubType.name}`,
      linkIntent: "navigational",
      linkScope: "button",
    });

    if (isDoctorConsultType(voucherSubType.voucherVendorDefinitionId)) {
      onDoctorConsultClick(voucherSubType);
    } else if (
      voucherSubType.voucherVendorDefinitionId ===
      VoucherVendorDefinitionId.NURSE_CONSULT
    ) {
      onNurseConsultClick(voucherSubType);
    } else if (
      voucherSubType.voucherVendorDefinitionId ===
      VoucherVendorDefinitionId.DOCTOR_MEDICINE_CONSULT
    ) {
      onDocMedBundleConsultClick(voucherSubType);
    }
  }

  function transformVoucherSubTypeForTranslationKeys(
    voucherVendorDefinitionId: string
  ): string | null {
    if (isDoctorConsultType(voucherVendorDefinitionId)) {
      return "doctorConsult";
    } else if (
      voucherVendorDefinitionId === VoucherVendorDefinitionId.NURSE_CONSULT
    ) {
      return "nurseConsult";
    } else if (
      voucherVendorDefinitionId ===
      VoucherVendorDefinitionId.DOCTOR_MEDICINE_CONSULT
    ) {
      return "docMedBundle";
    }
    return null;
  }

  useEffect(() => {
    trackPageViewV2({
      pageName: "Vouchering:Doctor nurse voucher type selection",
      pageSubSection1: "Vouchering",
      pageSubSection2: "Vouchering:Doctor nurse voucher type selection",
      pageCategory: "Vouchering",
    });
  }, []);

  return (
    <Stack
      color="neutral.dark"
      sx={{
        ...defaultFullscreenPageStyling,
        minHeight: "100%",
        justifyContent: "space-between",
        flex: 1,
      }}
    >
      <Stack p={2} spacing={2}>
        <Stack>
          <CircledBackButton showLabel={true} onClick={onBackButtonClick} />
        </Stack>
        <Stack>
          <Typography variant="h1" color="inherit">
            {t("DoctorNurseVoucherTypeSelection.title")}
          </Typography>

          <Stack spacing={4} py={4}>
            {selectedVoucher?.voucherSubTypes
              ?.sort((a, b) => a.order - b.order)
              .map((voucherSubType) => {
                const voucherSubTypeKey =
                  transformVoucherSubTypeForTranslationKeys(
                    voucherSubType.voucherVendorDefinitionId
                  );
                return (
                  <VoucherCard
                    key={voucherSubType.voucherSubTypeId}
                    voucherSubType={voucherSubType}
                    voucherSubTypeKey={voucherSubTypeKey}
                    onClick={handleVoucherSubTypeClick}
                  />
                );
              })}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
